import { useMemo } from 'react'
import config from 'constants/config'
import type { OfferStructuredDataPrice } from 'components/utils/OfferStructuredData'
import { queryKeySourceFees, queryKeySourceTax, queryKeySourceTotal } from 'constants/url'
import { isGhaVerifierUserAgent } from 'lib/metasearch/ghaLoadingDiagnostics'
import { parseGhaAmountParam } from 'lib/metasearch/ghaPriceMatch'
import { stringParam } from 'lib/url/queryStringUtils'
import { useAppSelector } from './reduxHooks'

export interface GhaVerifierPricing {
  /** Google's displayed total: base rate + tax + fees. */
  total: number
  tax: number
  fees: number
}

/**
 * The price breakdown Google is already showing for this landing, from the `source*` params —
 * its `(PRICE-DISPLAYED-TOTAL)`, `(PRICE-DISPLAYED-TAX)` and `(PRICE-DISPLAYED-FEES)` variables.
 *
 * Read as one set, never mixed with a price derived from rates: Google builds the total as
 * base + tax + fees, so taking all three together is the only way the figures on the page still
 * add up. A landing whose URL is missing any of them gets no override at all rather than a
 * part-derived breakdown.
 *
 * Only the price-verification bot sees this — the override is keyed on its user agent, with
 * `priceCheck=true` standing in for manual testing as it already does in `getSource`. Every real
 * visitor keeps the rate price and breakdown they would have seen.
 */
export function useGhaVerifierPricing(): GhaVerifierPricing | undefined {
  return useAppSelector(state => {
    if (!config.GHA_VERIFIER_USE_URL_PRICE_ENABLED) {
      return undefined
    }
    const { query } = state.router.location
    if (!isGhaVerifierUserAgent(state) && query.priceCheck !== 'true') {
      return undefined
    }

    const total = parseGhaAmountParam(stringParam(query[queryKeySourceTotal]))
    const tax = parseGhaAmountParam(stringParam(query[queryKeySourceTax]))
    const fees = parseGhaAmountParam(stringParam(query[queryKeySourceFees]))
    if (!total || total <= 0 || tax === undefined || fees === undefined) {
      return undefined
    }

    return { total, tax, fees }
  })
}

/**
 * A display price, replaced by {@link useGhaVerifierPricing}'s total while the verifier is looking.
 * Wrap a final public price with this; member prices are left alone, since the bot is never signed
 * in and the member figure is not what it verifies.
 *
 * Pass `disabled` wherever the page shows prices for more than the one property Google priced — a
 * metasearch landing's secondary tiles are other hotels, and Google's total says nothing about them.
 */
export default function useGhaVerifierPrice(price: number, disabled?: boolean): number {
  const verifierPricing = useGhaVerifierPricing()
  return disabled ? price : verifierPricing?.total ?? price
}

/**
 * Google's own breakdown in the shape `createOfferStructuredData` takes, so the offer it reads out
 * of our `Hotel` markup carries the same figures as the price rendered on the page. Only the
 * prominent offer takes this — the `allOffers` entries beside it describe other rates, which keep
 * their own prices exactly as the on-screen rate list does.
 */
export function useGhaVerifierStructuredPrice(): OfferStructuredDataPrice | undefined {
  const pricing = useGhaVerifierPricing()

  return useMemo(() => pricing && {
    offerPrice: pricing.total,
    offerPriceBeforeTax: pricing.total - pricing.tax - pricing.fees,
    taxesAndFees: pricing.tax,
    propertyFees: pricing.fees,
  }, [pricing])
}

/**
 * The same params as they sit in the URL, for forwarding onto an offer page click-through — the
 * landing and the page it links to must verify against one set of figures. Values pass through
 * untouched; whether they are usable is {@link useGhaVerifierPricing}'s call, not this one's.
 */
export function useGhaDisplayedPriceParams(): Record<string, string> | undefined {
  const total = useAppSelector(state => state.router.location.query[queryKeySourceTotal])
  const tax = useAppSelector(state => state.router.location.query[queryKeySourceTax])
  const fees = useAppSelector(state => state.router.location.query[queryKeySourceFees])

  return useMemo(() => {
    const present = Object.entries({
      [queryKeySourceTotal]: total,
      [queryKeySourceTax]: tax,
      [queryKeySourceFees]: fees,
    }).filter((entry): entry is [string, string] => typeof entry[1] === 'string' && !!entry[1])

    return present.length ? Object.fromEntries(present) : undefined
  }, [total, tax, fees])
}
